/** Cookie set after the customer confirms a localization choice so the prompt is not shown again. */
const FORCE_LOCALIZATION_COOKIE = 'forceLocalization';

const COOKIE_MAX_AGE_SECONDS = 180 * 86400;

/** Replaced in the title template after Liquid renders the storefront country name. */
const DETECTED_COUNTRY_PLACEHOLDER = '[[DETECTED_COUNTRY]]';

/**
 * @param {string} name
 * @returns {string | undefined}
 */
function getCookie(name) {
  const escapedName = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} maxAgeSeconds
 */
function setCookie(name, value, maxAgeSeconds) {
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
}

/**
 * Response shape for `GET /browsing_context_suggestions.json`.
 *
 * @typedef {object} BrowsingContextSuggestions
 * @property {{ country?: { name?: string; handle?: string } }} [detected_values]
 */

/**
 * Modal that prompts customers when their browsing region does not match the storefront country.
 */
class CountryDialog extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {{ handle: string; name?: string } | null} */
  #detectedCountry = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const {signal} = this.#abortController;

    this.querySelector('[data-country-dialog-close]')?.addEventListener('click', () => this.#closeDialog(), {signal});
    this.querySelector('[data-localization-back]')?.addEventListener('click', () => this.#handleBackToDetectedCountry(), {signal});

    const dialog = this.#getDialog();
    dialog?.addEventListener('close', () => document.documentElement.classList.remove('lock'), {signal});

    void this.#checkCountry({signal});
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    document.documentElement.classList.remove('lock');
  }

  /**
   * @returns {HTMLDialogElement | null}
   */
  #getDialog() {
    const dialog = this.querySelector('dialog');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  #closeDialog() {
    this.#getDialog()?.close();
  }

  #showDialog() {
    const dialog = this.#getDialog();
    if (!dialog) return;

    dialog.showModal();
    document.documentElement.classList.add('lock');
  }

  /**
   * Persists opt-out so the redirect dialog is not shown again after a choice is submitted.
   */
  #handleLocalizationSubmit = () => {
    setCookie(FORCE_LOCALIZATION_COOKIE, 'true', COOKIE_MAX_AGE_SECONDS);
  };

  /**
   * Selects the browser-detected country in the hidden select and submits the localization form.
   */
  #handleBackToDetectedCountry() {
    const detected = this.#detectedCountry;
    const countrySelect = this.querySelector('#CountryRedirectCountrySelect');
    const submitButton = this.querySelector('[data-localization-submit]');

    if (!detected?.handle || !(countrySelect instanceof HTMLSelectElement) || !(submitButton instanceof HTMLButtonElement)) {
      return;
    }

    countrySelect.value = detected.handle;
    submitButton.click();
  }

  /**
   * Fetches Shopify browsing context, compares detected country to the active storefront country,
   * and opens the dialog when they differ.
   *
   * @param {{ signal: AbortSignal }} options
   */
  async #checkCountry({signal}) {
    if (getCookie(FORCE_LOCALIZATION_COOKIE)) return;

    const userCountry = this.dataset.userCountry?.trim();
    if (!userCountry) return;

    /** @type {BrowsingContextSuggestions | undefined} */
    let result;

    try {
      const response = await fetch('/browsing_context_suggestions.json', {
        signal,
        headers: {Accept: 'application/json'},
      });
      if (!response.ok) return;
      result = await response.json();
    } catch {
      return;
    }

    const navigationCountry = result?.detected_values?.country;
    if (!navigationCountry?.handle) return;

    if (this.#isSameCountry(navigationCountry.handle, userCountry)) return;

    this.#detectedCountry = navigationCountry;

    const detectedName = navigationCountry.name?.trim() ?? '';
    const heading = this.querySelector('#CountryRedirectHeading');
    const titleTemplate = this.querySelector('[data-title-template]');
    const template = titleTemplate?.textContent?.trim() ?? '';

    if (heading && template) {
      heading.textContent = template.replaceAll(DETECTED_COUNTRY_PLACEHOLDER, detectedName);
    }

    if (detectedName) {
      for (const el of this.querySelectorAll('[data-country]')) {
        el.textContent = detectedName;
      }
    }

    const form = this.querySelector('#CountryRedirectForm');
    form?.addEventListener('submit', this.#handleLocalizationSubmit, {signal});

    this.#showDialog();
  }

  /**
   * @param {string} detectedHandle - Value from browsing context (typically ISO 3166-1 alpha-2).
   * @param {string} shopCountryCode - `localization.country.iso_code` from Liquid.
   * @returns {boolean}
   */
  #isSameCountry(detectedHandle, shopCountryCode) {
    return detectedHandle.trim().toUpperCase() === shopCountryCode.trim().toUpperCase();
  }
}

if (!customElements.get('country-dialog')) {
  customElements.define('country-dialog', CountryDialog);
}
